import * as stylex from "@stylexjs/stylex";
import {
  colors,
  fontSizes,
  spacing,
} from "@softmax/design-system/tokens.stylex";

export type PlayActivityGroup = {
  readonly key: string;
  readonly heading: string;
  readonly rows: readonly {
    readonly key: string;
    readonly when: string;
    readonly policyLabel: string;
    readonly text: string;
    readonly champion: boolean;
    readonly href: string | null;
  }[];
};

export function PlayActivityFeed({
  groups,
}: {
  readonly groups: readonly PlayActivityGroup[];
}) {
  if (groups.length === 0)
    return <p {...stylex.props(styles.empty)}>Nothing in the last 7 days.</p>;

  return (
    <div {...stylex.props(styles.groups)}>
      {groups.map((group) => (
        <section key={group.key}>
          <h3 {...stylex.props(styles.heading)}>{group.heading}</h3>
          {group.rows.map((row) => {
            const content = (
              <>
                <span {...stylex.props(styles.when)}>{row.when}</span>
                <span {...stylex.props(styles.policy)} title={row.policyLabel}>
                  {row.policyLabel}
                </span>
                <span {...stylex.props(styles.description)}>{row.text}</span>
                {row.champion ? (
                  <span {...stylex.props(styles.champion)}>Champion</span>
                ) : null}
              </>
            );
            return row.href ? (
              <a
                key={row.key}
                href={row.href}
                {...stylex.props(styles.row, styles.link)}
              >
                {content}
              </a>
            ) : (
              <div key={row.key} {...stylex.props(styles.row)}>
                {content}
              </div>
            );
          })}
        </section>
      ))}
    </div>
  );
}

const narrow = "@container (max-width: 479px)";
const styles = stylex.create({
  groups: {
    containerType: "inline-size",
    display: "flex",
    minHeight: 0,
    flex: 1,
    flexDirection: "column",
    gap: spacing.space16,
  },
  heading: {
    margin: 0,
    paddingBottom: spacing.space8,
    fontSize: fontSizes.caption,
    fontWeight: 400,
    lineHeight: "16px",
    color: colors.muted,
  },
  row: {
    display: "grid",
    gridTemplateColumns: {
      default: "64px minmax(0, 0.7fr) minmax(0, 1fr) auto",
      [narrow]: "minmax(0, 1fr) auto",
    },
    alignItems: "center",
    gap: spacing.space8,
    paddingBlock: spacing.space8,
    borderBottomWidth: { default: 1, ":last-child": 0 },
    borderBottomStyle: "solid",
    borderBottomColor: colors.border,
    color: colors.foreground,
    fontSize: fontSizes.label,
    lineHeight: "19px",
  },
  link: {
    textDecoration: "none",
    backgroundColor: { default: "transparent", ":hover": colors.surfaceAlt },
    ":focus-visible": {
      outlineWidth: 2,
      outlineStyle: "solid",
      outlineColor: colors.foreground,
      outlineOffset: 2,
    },
  },
  when: {
    fontSize: fontSizes.caption,
    lineHeight: "16px",
    color: colors.muted,
    gridColumn: { default: "auto", [narrow]: "1" },
    gridRow: { default: "auto", [narrow]: "1" },
  },
  policy: {
    minWidth: 0,
    overflowWrap: "anywhere",
    gridColumn: { default: "auto", [narrow]: "1 / -1" },
  },
  description: {
    minWidth: 0,
    overflowWrap: "anywhere",
    gridColumn: { default: "auto", [narrow]: "1 / -1" },
  },
  champion: {
    justifySelf: "end",
    borderRadius: 3,
    backgroundColor: colors.action,
    paddingInline: 6,
    paddingBlock: 2,
    fontSize: fontSizes.caption,
    fontWeight: 500,
    lineHeight: "15px",
    letterSpacing: "0.48px",
    gridColumn: { default: "auto", [narrow]: "2" },
    gridRow: { default: "auto", [narrow]: "1" },
  },
  empty: {
    margin: 0,
    paddingBlock: 6,
    fontSize: fontSizes.small,
    fontStyle: "italic",
    color: colors.muted,
  },
});
